/**
 * Mini App: как каркас узнаёт, что он открыт внутри Telegram, и что при
 * этом меняется (§6.1, §6.2, §13.2 ТЗ платформы).
 *
 * Меняются ровно две вещи, и обе — про вход:
 *
 * 1. Личность приходит из `initData`, а не из cookie сессии. Каркас
 *    прикладывает её к каждому запросу заголовком, сервер проверяет
 *    подпись (`require_service_miniapp`, §6.1). Сессия при этом не
 *    создаётся вовсе — §6.2 прямо: «Mini App сессии не создаёт».
 * 2. Кнопки входа через Telegram Login Widget не показывается: человек
 *    уже в Telegram, показывать ему кнопку «войти через Telegram» —
 *    значит просить войти туда, где он находится.
 *
 * **Почему не `@telegram-apps/sdk-react`, названный в §13.2.** Всё, что
 * нужно каркасу от Telegram, — строка `initData` и сигнал «приложение
 * готово». Их даёт официальный `telegram-web-app.js`, который Telegram
 * всё равно требует подключить и который приходит с его домена одним
 * тегом `<script>`. Библиотека поверх него добавила бы зависимость,
 * которую заказчику придётся обновлять самому (D5), не добавив ни одной
 * возможности из тех, что нам нужны. Появится нужда в остальном
 * (тактильная отдача, кнопки Telegram, темы) — это отдельное изменение с
 * проверкой на трёх сервисах.
 */

/** Та часть `window.Telegram.WebApp`, которой пользуется каркас. */
export interface TelegramWebApp {
  /** Подписанная строка Telegram. Пустая — приложение открыто не из бота. */
  initData: string;
  /** «Каркас отрисован» — Telegram убирает заставку. */
  ready?: () => void;
  /** Развернуть на всю высоту: список блюд в половину экрана бесполезен. */
  expand?: () => void;
  /** Не закрывать приложение смахиванием вниз (Bot API 7.7). */
  disableVerticalSwipes?: () => void;
  /**
   * Открыть ссылку Telegram (бот, чат) — не покидая Telegram. Нужна, чтобы
   * вести в другой бот: переход на чужой адрес внутри Mini App терял бы
   * подписанную личность (§6.1).
   */
  openTelegramLink?: (url: string) => void;
  colorScheme?: 'light' | 'dark';
}

interface TelegramNamespace {
  WebApp?: TelegramWebApp;
}

export function telegramWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const telegram = (window as unknown as { Telegram?: TelegramNamespace }).Telegram;
  return telegram?.WebApp ?? null;
}

/**
 * Подписанные данные о том, кто открыл приложение, — или `null`.
 *
 * Пустая строка приравнивается к отсутствию: так выглядит Mini App,
 * открытый по прямой ссылке в браузере, а не из бота. Доверять этой
 * строке нельзя ни при каких обстоятельствах — её проверяет сервер
 * (§6.1); здесь она только передаётся дальше.
 */
export function getInitData(): string | null {
  const raw = telegramWebApp()?.initData;
  return raw ? raw : null;
}

/** Открыт ли интерфейс внутри Telegram — от этого зависит способ входа. */
export function isMiniApp(): boolean {
  return getInitData() !== null;
}

/**
 * Сказать Telegram, что можно убирать заставку, развернуть окно и не
 * закрывать его смахиванием вниз.
 * Вызывается один раз при запуске сборки сервиса; вне Telegram не делает
 * ничего.
 */
export function initMiniApp(): void {
  const app = telegramWebApp();
  app?.ready?.();
  app?.expand?.();
  // Смахивание вниз закрывает Mini App — а человек, листающий длинный
  // список чеков, делает ровно это движение, и приложение пряталось у
  // него из-под пальца (владелец, 11.09.2026). Закрыть по-прежнему можно
  // кнопкой Telegram; у старых клиентов метода нет — и ничего не делаем.
  app?.disableVerticalSwipes?.();
}
